// Mock API service for OTP functionality and onboarding
// Replace these with your actual API endpoints

interface SendOtpResponse {
  success: boolean;
  message: string;
}

interface VerifyOtpResponse {
  success: boolean;
  message: string;
}

interface OnboardingData {
  // Company Info
  companyName: string;
  businessNumber: string;
  legalName: string;
  operatingName: string;
  
  // Address
  streetNumber: string;
  streetName: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  
  // Tax & Merchant Info
  gstNumber: string;
  qstNumber: string;
  authorizationCode: string;
  cloverMID: string;
}

interface OnboardingResponse {
  success: boolean;
  message: string;
  companyId?: number;
  customerId?: number;
}

interface QstValidationPayload {
  // Define the expected fields for QST validation
  qstNumber: string;
  gstNumber: string;
  businessName?: string;
  [key: string]: any;
}

// Get API base URL from environment variable
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/mainintel';

export const api = {

  // Submit onboarding data
  async submitOnboardingData(data: OnboardingData): Promise<OnboardingResponse> {
    const response = await fetch(`${API_BASE_URL}/api/create-company-and-customer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})); // try to get error message from body
      throw new Error(errorData.message || 'Failed to submit onboarding data');
    }

    const responseData = await response.json();
    return {
      success: true,
      message: 'Onboarding data submitted successfully',
      companyId: responseData.companyId,
      customerId: responseData.customerId,
    };
  },

  // Send OTP to email (example for real API)
  
  async sendOtp(email: string): Promise<SendOtpResponse> {
    const response = await fetch(`${API_BASE_URL}/api/send-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) {
      throw new Error('Failed to send OTP');
    }
    return response.json();
  },

  // Verify OTP (example for real API)
  async verifyOtp(email: string, otp: string): Promise<VerifyOtpResponse> {
    const response = await fetch(`${API_BASE_URL}/api/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, otp }),
    });
    if (!response.ok) {
      throw new Error('Failed to verify OTP');
    }
    return response.json();
  },

  async createMailingContact(email: string, companyName: string, customerName: string): Promise<VerifyOtpResponse> {
    const response = await fetch(`${API_BASE_URL}/api/create-mailing-contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, companyName, customerName }),
    });
    if (!response.ok) {
      throw new Error('Failed to create the mailing contact');
    }
    return response.json();
  },

  // Check if email is already taken
  async checkEmail(email: string): Promise<{ taken: boolean; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/api/check-email/${encodeURIComponent(email)}`);
    if (!response.ok) {
      throw new Error('Failed to check email');
    }
    const data = await response.json();
    return { taken: !!data.exists, message: data.exists ? 'This email is already registered. Please use a different email.' : undefined };
  },

  // Subscription: Create subscription
  async createSubscription(payload: { customerId: number; subscription: string; oneTimeFee: boolean }): Promise<{ subscriptionId: string }> {
    const response = await fetch(`${API_BASE_URL}/api/create-subscription`, { 
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('Failed to create subscription');
    return response.json();
  },

  // Subscription: Email subscription
  async emailSubscription(subscriptionId: string): Promise<void> {
    await fetch(`${API_BASE_URL}/api/subscription/${subscriptionId}/email`, { method: 'POST' });
  },

  // Validate QST details
  async validateQst(payload: QstValidationPayload): Promise<{ success: boolean; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/validate-rq-details`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.message || 'QST Validation Failed');
    return data;
  },

  // Create user
  async createUser(payload: { customerId: number; name: string; email: string; password: string }): Promise<{ success: boolean; userId?: number; message?: string }> {
    const response = await fetch(`${API_BASE_URL}/api/create-user`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to create user');
    }
    return response.json();
  }
};

export const sendOTP = async (email: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/send-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      message: 'Failed to send OTP',
    };
  }
};

export const verifyOTP = async (email: string, otp: string): Promise<{ success: boolean; message: string }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/verify-otp`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, otp }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    return {
      success: false,
      message: 'Failed to verify OTP',
    };
  }
}; 